import { useState, useRef, useCallback, useEffect } from "react";

interface UseVideoRecorderReturn {
	// State
	isRecording: boolean;
	isPaused: boolean;
	isProcessing: boolean;
	recordedBlob: Blob | null;
	recordedUrl: string | null;
	error: string | null;
	hasPermission: boolean | null;
	isInitializing: boolean;
	recordingTime: number;

	// Stream ref for video preview
	streamRef: React.RefObject<MediaStream | null>;

	// Actions
	initializeCamera: () => Promise<boolean>;
	startRecording: () => void;
	stopRecording: () => void;
	pauseRecording: () => void;
	resumeRecording: () => void;
	resetRecording: () => void;
	cleanup: () => void;
}

export function useVideoRecorder(): UseVideoRecorderReturn {
	const [isRecording, setIsRecording] = useState(false);
	const [isPaused, setIsPaused] = useState(false);
	const [isProcessing, setIsProcessing] = useState(false);
	const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
	const [recordedUrl, setRecordedUrl] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [hasPermission, setHasPermission] = useState<boolean | null>(null);
	const [isInitializing, setIsInitializing] = useState(false);
	const [recordingTime, setRecordingTime] = useState(0);

	const streamRef = useRef<MediaStream | null>(null);
	const mediaRecorderRef = useRef<MediaRecorder | null>(null);
	const chunksRef = useRef<Blob[]>([]);
	const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

	const cleanup = useCallback(() => {
		// Stop timer
		if (timerRef.current) {
			clearInterval(timerRef.current);
			timerRef.current = null;
		}

		// Stop media recorder
		if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
			mediaRecorderRef.current.stop();
		}
		mediaRecorderRef.current = null;

		// Stop all tracks
		if (streamRef.current) {
			streamRef.current.getTracks().forEach((track) => track.stop());
			streamRef.current = null;
		}

		// Revoke URL
		if (recordedUrl) {
			URL.revokeObjectURL(recordedUrl);
		}

		chunksRef.current = [];
	}, [recordedUrl]);

	const initializeCamera = useCallback(async (): Promise<boolean> => {
		setIsInitializing(true);
		setError(null);

		try {
			const stream = await navigator.mediaDevices.getUserMedia({
				video: {
					width: { ideal: 1280 },
					height: { ideal: 720 },
					facingMode: "user",
				},
				audio: true,
			});

			streamRef.current = stream;
			setHasPermission(true);
			setIsInitializing(false);
			return true;
		} catch (err) {
			setHasPermission(false);
			setIsInitializing(false);

			if (err instanceof Error) {
				if (err.name === "NotAllowedError") {
					setError("Camera access denied. Please allow camera and microphone access.");
				} else if (err.name === "NotFoundError") {
					setError("No camera or microphone found on this device.");
				} else {
					setError(`Failed to access camera: ${err.message}`);
				}
			} else {
				setError("Failed to access camera. Please try again.");
			}
			return false;
		}
	}, []);

	const startRecording = useCallback(() => {
		if (!streamRef.current) {
			setError("Camera not initialized. Please try again.");
			return;
		}

		chunksRef.current = [];
		setError(null);

		try {
			const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
				? "video/webm;codecs=vp9,opus"
				: MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus")
					? "video/webm;codecs=vp8,opus"
					: "video/webm";

			const mediaRecorder = new MediaRecorder(streamRef.current, {
				mimeType,
				videoBitsPerSecond: 2500000,
			});

			mediaRecorder.ondataavailable = (event) => {
				if (event.data.size > 0) {
					chunksRef.current.push(event.data);
				}
			};

			mediaRecorder.onstop = () => {
				const blob = new Blob(chunksRef.current, { type: mimeType });
				setRecordedBlob(blob);
				const url = URL.createObjectURL(blob);
				setRecordedUrl(url);
				setIsProcessing(false);
			};

			mediaRecorder.onerror = () => {
				setError("Recording failed. Please try again.");
				setIsRecording(false);
				setIsProcessing(false);
			};

			mediaRecorderRef.current = mediaRecorder;
			mediaRecorder.start(1000); // Collect data every second
			setIsRecording(true);
			setRecordingTime(0);

			// Start timer
			timerRef.current = setInterval(() => {
				setRecordingTime((prev) => prev + 1);
			}, 1000);
		} catch (err) {
			setError("Failed to start recording. Please try again.");
		}
	}, []);

	const stopRecording = useCallback(() => {
		if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
			setIsProcessing(true);
			mediaRecorderRef.current.stop();
		}
		setIsRecording(false);
		setIsPaused(false);

		if (timerRef.current) {
			clearInterval(timerRef.current);
			timerRef.current = null;
		}
	}, []);

	const pauseRecording = useCallback(() => {
		if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
			mediaRecorderRef.current.pause();
			setIsPaused(true);

			if (timerRef.current) {
				clearInterval(timerRef.current);
				timerRef.current = null;
			}
		}
	}, []);

	const resumeRecording = useCallback(() => {
		if (mediaRecorderRef.current && mediaRecorderRef.current.state === "paused") {
			mediaRecorderRef.current.resume();
			setIsPaused(false);

			timerRef.current = setInterval(() => {
				setRecordingTime((prev) => prev + 1);
			}, 1000);
		}
	}, []);

	const resetRecording = useCallback(() => {
		// Stop recording if active
		if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
			mediaRecorderRef.current.stop();
		}

		// Clear timer
		if (timerRef.current) {
			clearInterval(timerRef.current);
			timerRef.current = null;
		}

		// Revoke old URL
		if (recordedUrl) {
			URL.revokeObjectURL(recordedUrl);
		}

		// Reset state
		chunksRef.current = [];
		setRecordedBlob(null);
		setRecordedUrl(null);
		setIsRecording(false);
		setIsPaused(false);
		setIsProcessing(false);
		setRecordingTime(0);
		setError(null);
	}, [recordedUrl]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			cleanup();
		};
	}, [cleanup]);

	return {
		isRecording,
		isPaused,
		isProcessing,
		recordedBlob,
		recordedUrl,
		error,
		hasPermission,
		isInitializing,
		recordingTime,
		streamRef,
		initializeCamera,
		startRecording,
		stopRecording,
		pauseRecording,
		resumeRecording,
		resetRecording,
		cleanup,
	};
}
